/** Shipped query templates (spec §15.8). `<urn:km:blk:…>` placeholders take the selected node. */
const P = `PREFIX km:   <https://w3id.org/kss/ns#>
PREFIX rdf:  <http://www.w3.org/1999/02/22-rdf-syntax-ns#>
PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>
PREFIX obo:  <http://purl.obolibrary.org/obo/>
PREFIX oa:   <http://www.w3.org/ns/oa#>
`;

export interface Template {
  id: string;
  /** an i18n key (spec §8.23) */
  labelKey: string;
  query(ctx: { block?: string; doc?: string }): string;
}

/** The canonical example of spec §15.4, verbatim. */
export const CANONICAL = `${P}
SELECT ?s ?p ?o ?g ?conf WHERE {
  GRAPH ?g { ?s ?p ?o }
  FILTER(?p = km:mentions)
  ?o rdfs:subClassOf* obo:CHEBI_24431 .                 # chemical entity and subclasses
  OPTIONAL { GRAPH ?g { ?r rdf:reifies <<( ?s ?p ?o )>> ; km:confidence ?conf } }
}
LIMIT 2000`;

export const TEMPLATES: Template[] = [
  { id: "chebi", labelKey: "graph.template.chebi", query: () => CANONICAL },
  {
    id: "neighbourhood",
    labelKey: "graph.template.neighbourhood",
    query: ({ block = "urn:km:blk:DOC:TREE" }) => `${P}
SELECT ?s ?p ?o ?g ?conf WHERE {
  { GRAPH ?g { <${block}> ?p ?o } BIND(<${block}> AS ?s) }
  UNION
  { GRAPH ?g { ?s ?p <${block}> } BIND(<${block}> AS ?o) }
  FILTER(?p != rdf:reifies)
  OPTIONAL { GRAPH ?g { ?r rdf:reifies <<( ?s ?p ?o )>> ; km:confidence ?conf } }
}
LIMIT 500`,
  },
  {
    id: "page-links",
    labelKey: "graph.template.pageLinks",
    query: ({ doc = "DOC" }) => `${P}
SELECT ?s ?p ?o ?g WHERE {
  GRAPH ?g { ?r rdf:reifies <<( ?s ?p ?o )>> }
  FILTER(?g = <urn:km:graph:asserted:${doc}>)
}
LIMIT 2000`,
  },
  {
    id: "blocks-by-page",
    labelKey: "graph.template.blocksByPage",
    query: () => `${P}
SELECT ?s ?p ?o ?g WHERE {
  GRAPH ?g { ?s km:inDocument ?o }
  BIND(km:inDocument AS ?p)
}
LIMIT 2000`,
  },
  {
    id: "sequences",
    labelKey: "graph.template.sequences",
    query: () => `${P}
SELECT ?s ?p ?o ?g ?position WHERE {
  GRAPH ?g { ?m km:inSequence ?s ; km:member ?o ; km:position ?position }
  BIND(km:member AS ?p)
}
ORDER BY ?s ?position
LIMIT 2000`,
  },
  {
    id: "derived",
    labelKey: "graph.template.derivedPending",
    query: () => `${P}
SELECT ?s ?p ?o ?g ?conf WHERE {
  GRAPH ?g { ?s ?p ?o }
  FILTER(STRSTARTS(STR(?g), "urn:km:graph:derived:") && ?p != rdf:reifies)
  OPTIONAL { GRAPH ?g { ?r rdf:reifies <<( ?s ?p ?o )>> ; km:confidence ?conf } }
}
LIMIT 2000`,
  },
  {
    // [M32] each structure and reaction participant with its InChIKey, the CHEMINF descriptor (spec §11.6)
    id: "inchikeys",
    labelKey: "graph.template.inchikeys",
    query: () => `${P}PREFIX cheminf: <http://semanticscience.org/resource/>

SELECT ?s ?p ?o ?g ?oLabel WHERE {
  GRAPH ?g { ?s cheminf:CHEMINF_000200 ?o . ?o a cheminf:CHEMINF_000059 ; cheminf:CHEMINF_000012 ?oLabel }
  BIND(cheminf:CHEMINF_000200 AS ?p)
}
LIMIT 2000`,
  },
  {
    id: "annotations",
    labelKey: "graph.template.annotations",
    query: ({ block = "urn:km:blk:DOC:TREE" }) => `${P}
SELECT ?s ?p ?o ?g WHERE {
  GRAPH ?g { ?s oa:hasTarget ?t . ?t oa:hasSource <${block}> }
  BIND(oa:hasTarget AS ?p)
  BIND(<${block}> AS ?o)
}
LIMIT 500`,
  },
];
